import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import '../../styles/BlogDetail.css';
import {
  FaArrowLeft,
  FaThumbsUp,
  FaComments,
  FaEye,
  FaClock,
  FaUser,
  FaTrash,
  FaEdit
} from 'react-icons/fa';

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [liked, setLiked] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [deleteCommentId, setDeleteCommentId] = useState(null);

  const fetchBlog = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/blogs/${id}`
      );

      if (response.data.success) {
        setBlog(response.data.data);
        const user = JSON.parse(sessionStorage.getItem('user'));
        setLiked(response.data.data.likedBy?.includes(user?.id));
        setError('');
      }
    } catch (err) {
      setError('Failed to load blog');
      console.error('Error fetching blog:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem('user'));
    setCurrentUser(user);
    fetchBlog();
  }, [fetchBlog]);

  const handleLike = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/blogs/${id}/like`,
        {},
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setLiked(response.data.data.liked);
        setBlog(prev => ({
          ...prev,
          likes: response.data.data.likes
        }));
      }
    } catch (err) {
      console.error('Error liking blog:', err);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();

    if (!newComment.trim()) {
      return;
    }

    try {
      setCommentLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/blogs/${id}/comments`,
        { text: newComment },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setBlog(prev => ({
          ...prev,
          comments: [...prev.comments, response.data.data]
        }));
        setNewComment('');
      }
    } catch (err) {
      console.error('Error adding comment:', err);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/blogs/${id}/comments/${commentId}`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );

      setBlog(prev => ({
        ...prev,
        comments: prev.comments.filter(comment => comment._id !== commentId)
      }));
      setDeleteCommentId(null);
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="blog-detail-container2">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading blog...</p>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    const backPath = currentUser?.role === 'blogger' ? '/blogger/blogs' : '/super-admin/blogs';
    return (
      <div className="blog-detail-container">
        <button className="back-btn" onClick={() => navigate(backPath)}>
          <FaArrowLeft /> Back
        </button>
        <div className="error-state">
          <p>{error || 'Blog not found'}</p>
        </div>
      </div>
    );
  }

  const isAuthor = currentUser?.id === blog.author?._id;
  const backPath = currentUser?.role === 'blogger' ? '/blogger/blogs' : '/super-admin/blogs';
  const editPath = currentUser?.role === 'blogger' ? `/blogger/blogs/edit/${blog._id}` : `/super-admin/blogs/edit/${blog._id}`;

  return (
    <div className="blog-detail-container2">
      <div className="blog-detail-header">
        <button className="back-btn" onClick={() => navigate(backPath)}>
          <FaArrowLeft /> Back to Blogs
        </button>
        {isAuthor && (
          <div className="author-actions">
            <button
              className="btn-edit"
              onClick={() => navigate(editPath)}
            >
              <FaEdit /> Edit
            </button>
          </div>
        )}
      </div>

      {blog.featuredImage && (
        <div className="blog-featured-image-large">
          <img src={blog.featuredImage} alt={blog.title} />
        </div>
      )}

      <article className="blog-detail-article">
        <header className="blog-detail-header-section">
          <div className="blog-meta-top">
            <span className={`status-badge ${blog.status}`}>{blog.status}</span>
            {blog.category && <span className="category-badge">{blog.category}</span>}
          </div>

          <h1 className="blog-detail-title">{blog.title}</h1>

          {blog.description && (
            <p className="blog-detail-description">{blog.description}</p>
          )}

          <div className="blog-meta-info">
            <div className="meta-item">
              <FaUser /> <span>{blog.author?.name}</span>
            </div>
            <div className="meta-item">
              <FaClock /> <span>{formatDate(blog.createdAt)}</span>
            </div>
            <div className="meta-item">
              <FaEye /> <span>{blog.views} views</span>
            </div>
          </div>

          {blog.tags && blog.tags.length > 0 && (
            <div className="blog-tags-detail">
              {blog.tags.map((tag, index) => (
                <span key={index} className="tag">
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </header>

        <div
          className="blog-content"
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />

        <div className="blog-engagement">
          <button
            className={`engagement-btn like-btn ${liked ? 'liked' : ''}`}
            onClick={handleLike}
          >
            <FaThumbsUp /> {blog.likes} Likes
          </button>
          <div className="engagement-stat">
            <FaComments /> {blog.comments.length} Comments
          </div>
        </div>
      </article>

      <section className="blog-comments-section">
        <h2>Comments ({blog.comments?.length || 0})</h2>

        <form onSubmit={handleAddComment} className="comment-form">
          <div className="comment-input-wrapper">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Write a comment..."
              className="comment-input"
              rows="3"
            />
            <button
              type="submit"
              disabled={commentLoading || !newComment.trim()}
              className="btn-submit-comment"
            >
              {commentLoading ? 'Posting...' : 'Post Comment'}
            </button>
          </div>
        </form>

        <div className="comments-list">
          {blog.comments && blog.comments.length > 0 ? (
            blog.comments.map(comment => (
              <div key={comment._id} className="comment-item">
                <div className="comment-header">
                  <div className="comment-author-info">
                    <h4 className="comment-author">{comment.userName}</h4>
                    <span className="comment-date">
                      {formatDate(comment.createdAt)}
                    </span>
                  </div>
                  {(isAuthor || currentUser?.id === comment.userId?._id) && (
                    <div className="comment-actions">
                      {deleteCommentId === comment._id ? (
                        <button
                          className="btn-confirm-delete"
                          onClick={() => handleDeleteComment(comment._id)}
                        >
                          Confirm Delete
                        </button>
                      ) : (
                        <button
                          className="btn-delete-comment"
                          onClick={() => setDeleteCommentId(comment._id)}
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <p className="comment-text">{comment.text}</p>
              </div>
            ))
          ) : (
            <div className="no-comments">
              <p>No comments yet. Be the first to comment!</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogDetail;
